import React, { useState, useContext } from 'react';
import styled from 'styled-components';

import { ClientContactTag, IClientContact } from '@bb/contacts';

import { Input, Popconfirm, Text, theme, UserSearch } from 'components';
import { buildFullName } from 'utils';
import { AuthContext } from 'contexts';
import { zPositions } from '@/constants';
import { IAppointmentConfig } from '@bb/domain/dist/types';
import { useContactState, useContactModalState } from './hooks';
import { getDefaultContact, convertContactToLegacyContact } from './utils';
import { ContactStateType } from './types';
import { ContactModalMode } from './enums';
import { CONTACT_MODAL_POPCONFIRM_WIDTH } from './constants';
import { UserSearchType } from '../../components/UserSearch';

interface ContactSearchProps {
  contacts?: IAppointmentConfig['contacts'];
  addBuyerContact?: boolean;
}

const ContactSearch: React.FC<ContactSearchProps> = ({
  contacts,
  addBuyerContact,
}) => {
  const [contactState, updateContactState, , , feedbackSendToSeller] =
    useContactState();
  const [contactModalState, setContactModalState] = useContactModalState();
  const [selectedContact, setSelectedContact] = useState<IClientContact>(null);
  const { auth } = useContext(AuthContext);

  let searchType: UserSearchType = 'contact';

  if (auth.role === 'AGENT') searchType = 'contact';
  else if (
    contactModalState.listing?.reps?.find((rep: any) => rep?.id === auth.id)
  )
    searchType = 'contact';
  else if (auth.roles[auth.role] >= auth.roles.TEAM_ADMIN) {
    if (auth.isLicensed && addBuyerContact) searchType = 'contact';
    else searchType = 'adminContact';
  }

  const { firstName, lastName } = contactState;
  const { contactForEdit, contactInputsDisabled, isEditMode } =
    contactModalState;

  const handleOnEditContinue = () => {
    const mode = isEditMode
      ? ContactModalMode.EditAll
      : ContactModalMode.AddExistingEdited;
    setContactModalState({ contactInputsDisabled: false, mode });
  };

  const handleOnEditCancel = () => {
    let updatedContact: ContactStateType;
    if (contactForEdit) {
      updatedContact = {
        ...getDefaultContact(
          addBuyerContact ? 'Buyer' : 'Seller',
          feedbackSendToSeller,
        ),
        ...contactForEdit,
      };
    } else {
      updatedContact = convertContactToLegacyContact(
        selectedContact,
        getDefaultContact(
          addBuyerContact ? 'Buyer' : 'Seller',
          feedbackSendToSeller,
        ),
      );
    }

    const mode = isEditMode
      ? ContactModalMode.EditConfig
      : ContactModalMode.AddExisting;

    updateContactState(updatedContact);
    setContactModalState({ contactInputsDisabled: true, mode });
  };

  const handleOnContactChange = (contact?: IClientContact) => {
    if (!contact) {
      updateContactState(
        getDefaultContact(
          addBuyerContact ? 'Buyer' : 'Seller',
          feedbackSendToSeller,
        ),
      );
      setSelectedContact(null);
      setContactModalState({
        contactInputsDisabled: false,
        mode: ContactModalMode.AddNew,
      });
    } else {
      const updatedContact = convertContactToLegacyContact(
        contact,
        getDefaultContact(
          addBuyerContact ? 'Buyer' : 'Seller',
          feedbackSendToSeller,
        ),
      );
      updateContactState(updatedContact);
      setSelectedContact(contact);
      setContactModalState({
        contactInputsDisabled: true,
        mode: ContactModalMode.AddExisting,
        shouldClone: !contactModalState.listing.reps?.some(
          ({ _id }: any) => _id === contact.owner.id,
        ),
      });
    }
  };

  return (
    <StyledContactSearchContainer data-cy={'edit-contact-input-search'}>
      <StyledLabelContainer>
        <Text strong data-cy={'use-existing-title'}>
          Use Existing
        </Text>
        {(selectedContact || isEditMode) &&
          (contactInputsDisabled ? (
            <Popconfirm
              okText="Continue"
              onConfirm={() => handleOnEditContinue()}
              overlayStyle={{
                width: CONTACT_MODAL_POPCONFIRM_WIDTH,
                zIndex: zPositions.contactModalComponents,
              }}
              placement="topLeft"
              title="Saving this info will update the contact's information across the entire system."
            >
              <StyledClickableText data-cy="edit-contact-link">
                Edit contact
              </StyledClickableText>
            </Popconfirm>
          ) : (
            <StyledClickableText onClick={handleOnEditCancel}>
              Cancel
            </StyledClickableText>
          ))}
      </StyledLabelContainer>
      {isEditMode ? (
        <Input disabled defaultValue={buildFullName(firstName, lastName)} />
      ) : (
        <UserSearch
          filteredIds={(contacts || []).map(({ contactId }) => contactId)}
          onSelect={handleOnContactChange}
          onClear={handleOnContactChange}
          selectedUser={selectedContact}
          listingId={contactModalState.listingId}
          type={searchType}
          filters={{
            tags: Object.values(ClientContactTag).filter(el =>
              addBuyerContact
                ? el === ClientContactTag.Buyer
                : el !== ClientContactTag.Buyer,
            ),
          }}
        />
      )}
    </StyledContactSearchContainer>
  );
};

export default ContactSearch;

const StyledContactSearchContainer = styled.div`
  display: grid;
`;

const StyledLabelContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
`;

const StyledClickableText = styled.div`
  color: ${theme.blue6};
  cursor: pointer;
`;
